import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SettingsIcon from '@mui/icons-material/Settings';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fontSize, fontWeight, TypographyStyledGlobal } from '../../theme';
import { SelectedQuestion, SelectedQuestionSettings } from '../Modals/AddSurveyModal/addSurveyInterface';
import QuestionSettingsModal from './QuestionSettingsModal';
import {
    DragHandleStyled,
    SelectedQuestionHeaderStyled,
    SelectedQuestionInputStyled,
    SelectedQuestionItemStyled,
    SelectedQuestionsContainerStyled,
} from './styled';

interface SortableQuestionItemProps {
    onOpenSettings: (questionId: number) => void;
    onRemove: (questionId: number) => void;
    onRename: (questionId: number, name: string) => void;
    selectedQuestion: SelectedQuestion;
}

const SortableQuestionItem: FC<SortableQuestionItemProps> = ({
    onOpenSettings,
    onRemove,
    onRename,
    selectedQuestion,
}) => {
    const {
        attributes,
        isDragging,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: selectedQuestion.question.id });

    const style = {
        opacity: isDragging ? 0.5 : 1,
        position: isDragging ? ('relative' as const) : undefined,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <SelectedQuestionItemStyled>
                <DragHandleStyled {...listeners}>
                    <DragIndicatorIcon sx={{ color: colors.grey400, fontSize: 16 }} />
                </DragHandleStyled>
                <SelectedQuestionInputStyled
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onRename(selectedQuestion.question.id, e.target.value)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    value={selectedQuestion.customName}
                />
                <SettingsIcon
                    onClick={() => onOpenSettings(selectedQuestion.question.id)}
                    sx={{
                        color: colors.grey400,
                        cursor: 'pointer',
                        flexShrink: 0,
                        fontSize: 16,
                        '&:hover': { color: colors.grey200 },
                    }}
                />
                <DeleteOutlineIcon
                    onClick={() => onRemove(selectedQuestion.question.id)}
                    sx={{
                        color: colors.grey400,
                        cursor: 'pointer',
                        flexShrink: 0,
                        fontSize: 16,
                        '&:hover': { color: colors.alertDanger },
                    }}
                />
            </SelectedQuestionItemStyled>
        </div>
    );
};

interface SelectedQuestionsPanelProps {
    onConfigure: (questionId: number, customName: string, settings: SelectedQuestionSettings) => void;
    onRemove: (questionId: number) => void;
    onRename: (questionId: number, name: string) => void;
    onReorder: (questions: SelectedQuestion[]) => void;
    selectedQuestions: SelectedQuestion[];
    title?: string;
    hideSettings?: boolean;
    filterMode?: boolean;
}

const SelectedQuestionsPanel: FC<SelectedQuestionsPanelProps> = ({
    onConfigure,
    onRemove,
    onRename,
    onReorder,
    selectedQuestions,
    title,
    hideSettings = false,
    filterMode = false,
}) => {
    const { t } = useTranslation('settings');
    const [configuringQuestion, setConfiguringQuestion] = useState<SelectedQuestion | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = selectedQuestions.findIndex((q) => q.question.id === active.id);
            const newIndex = selectedQuestions.findIndex((q) => q.question.id === over.id);

            onReorder(arrayMove(selectedQuestions, oldIndex, newIndex));
        }
    };

    const handleOpenSettings = (questionId: number) => {
        const sq = selectedQuestions.find((q) => q.question.id === questionId) ?? null;

        setConfiguringQuestion(sq);
    };

    const handleSaveSettings = (questionId: number, customName: string, settings: SelectedQuestionSettings) => {
        onConfigure(questionId, customName, settings);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {title && (
                <SelectedQuestionHeaderStyled>
                    <TypographyStyledGlobal
                        color={colors.grey200}
                        size={fontSize.sm}
                        weight={fontWeight.semiBold}
                    >
                        {title}
                    </TypographyStyledGlobal>
                    <TypographyStyledGlobal color={colors.grey400} size={fontSize.s} weight={fontWeight.semiRegular}>
                        {t('tabs.createSurvey.step3Content.questionsCount', { count: selectedQuestions.length })}
                    </TypographyStyledGlobal>
                </SelectedQuestionHeaderStyled>
            )}
            <SelectedQuestionsContainerStyled>
                {selectedQuestions.length === 0 ? (
                    <TypographyStyledGlobal color={colors.grey400} size={fontSize.sm} style={{ padding: '8px 0' }}>
                        {t('tabs.createSurvey.step3Content.noSelectedQuestions')}
                    </TypographyStyledGlobal>
                ) : (
                    <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                        sensors={sensors}
                    >
                        <SortableContext
                            items={selectedQuestions.map((q) => q.question.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {selectedQuestions.map((sq) => (
                                <SortableQuestionItem
                                    key={sq.question.id}
                                    onOpenSettings={handleOpenSettings}
                                    onRemove={onRemove}
                                    onRename={onRename}
                                    selectedQuestion={sq}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                )}
            </SelectedQuestionsContainerStyled>

            {configuringQuestion && (
                <QuestionSettingsModal
                    filterMode={filterMode}
                    hideSettings={hideSettings}
                    onClose={() => setConfiguringQuestion(null)}
                    onSave={handleSaveSettings}
                    selectedQuestion={configuringQuestion}
                />
            )}
        </div>
    );
};

export default SelectedQuestionsPanel;
